import React from 'react';
import { DynamicPipelineAnalysisDemographicData } from 'libs/end-to-end/src/dynamic-pipeline-analysis';
import { DynamicPipelineAnalysisOutput } from 'libs/end-to-end/src/dynamic-pipeline-analysis';
import { DynamicPipelinePhaseDetails } from 'libs/end-to-end/src/dynamic-pipeline-analysis';
import { DynamicPipelinePhaseResult } from 'libs/end-to-end/src/dynamic-pipeline-analysis';
import { v4 as uuidv4 } from 'uuid';

export const gapBetweenDemographics = 8;
export const gapBetweenSections = 16;
export const phaseWidth = 176;
export const phasePaddingX = 8;
export const phasePaddingY = 8;
export const startOfSectionVerticalSpaceOccupied =
  phasePaddingY + 54 + gapBetweenDemographics;

export const gapBetweenPhases = (displayPhaseCount: number) => {
  const nonGapSpace = 1200 - phaseWidth * displayPhaseCount;
  const gapCount = displayPhaseCount - 1;
  const idealGap = Math.round(nonGapSpace / gapCount);
  return Math.min(idealGap, 237);
};

export interface DynamicPipelineAnalysisDemographicDataLayout
  extends DynamicPipelineAnalysisDemographicData {
  height: number;
  pct_of_group: number;
  pct_of_event: number;
  uuid: string;
}

export const computeDemographicDataBreakdownHeight = (props: {
  maxTotal: number;
  data: DynamicPipelineAnalysisDemographicData;
}): number => {
  const { maxTotal, data } = props;
  return Math.max((data.count / maxTotal) * 100, 10);
};

const estimateLayoutOfDemographicData = ({
  demographicData,
  maxTotal,
}: {
  demographicData: DynamicPipelineAnalysisDemographicData;
  maxTotal: number;
}): DynamicPipelineAnalysisDemographicDataLayout => {
  const height = computeDemographicDataBreakdownHeight({
    maxTotal,
    data: demographicData,
  });

  return {
    ...demographicData,
    pct_of_group: demographicData.ratio_of_group * 100,
    pct_of_event: demographicData.ratio_of_event * 100,
    height,
    uuid: uuidv4(),
  };
};

export interface DynamicPipelinePhaseResultLayout {
  total: number;
  avg_pct_of_event: number;
  demographic_data: DynamicPipelineAnalysisDemographicDataLayout[];
  // This refers to a translation phrase name. It may be dangerousUserInput, and if it is we render "customName". If it doesn't exist, we can fallback.
  name: string;
  customName: string;
}

const estimateLayoutOfPhaseResult = ({
  result,
  name,
}: {
  result: DynamicPipelinePhaseResult;
  name: string;
}): DynamicPipelinePhaseResultLayout => {
  const maxTotal = result.demographic_data.reduce(
    (acc, demographicData) => acc + demographicData.count,
    0,
  );

  const demographicDataArray: DynamicPipelineAnalysisDemographicDataLayout[] =
    [];

  result.demographic_data.forEach((demographicData) => {
    const layoutOfDemographicData = estimateLayoutOfDemographicData({
      demographicData,
      maxTotal,
    });

    demographicDataArray.push(layoutOfDemographicData);
  });

  return {
    ...result,
    avg_pct_of_event: result.avg_ratio_of_event * 100,
    demographic_data: demographicDataArray,
    name,
    customName: result.custom_name ?? '',
  };
};

const estimateLayoutOfPhaseDetails = ({
  phaseDetails,
}: {
  phaseDetails: DynamicPipelinePhaseDetails;
}): DynamicPipelinePhaseResultLayout[] => {
  const positiveResultLayout = estimateLayoutOfPhaseResult({
    result: phaseDetails.positive_result,
    name: phaseDetails.positive_result.name,
  });

  const negativeResultLayout = estimateLayoutOfPhaseResult({
    result: phaseDetails.negative_result,
    name: phaseDetails.negative_result.name,
  });

  return [positiveResultLayout, negativeResultLayout];
};

export const estimateLayoutOfPipelineAnalysis = ({
  analysisOutput,
}: {
  analysisOutput: DynamicPipelineAnalysisOutput;
}): DynamicPipelinePhaseResultLayout[][] => {
  const firstPhaseLayout = estimateLayoutOfPhaseResult({
    result: analysisOutput.firstPhase,
    name: analysisOutput.firstPhase.name,
  });

  const phasesLayout = analysisOutput.phases.map((phaseDetails, index) => {
    return estimateLayoutOfPhaseDetails({
      phaseDetails,
    });
  });

  return [[firstPhaseLayout], ...phasesLayout];
};
